import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as yup from 'yup'

import { TaxfyleJobStatus } from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { useFetchResponseWithUtils, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFetchError, invalidateFetch } from '@/reducers/fetch'
import {
  getContractorFilingJob,
  getContractorFilingQuestionnaire,
  SUBMIT_CONTRACTOR_FILING_QUESTIONNAIRE_KEY,
  submitContractorFilingQuestionnaire,
} from '@/features/taxes/annual-taxes/annualTaxFilings.slice'

export const ANNUAL_TAXES_PATH = '/taxes/annual'

const validationSchema = yup.object({
  businessName: yup.string().trim().required('Enter your business name.'),
  hasPayrollProvider: yup.boolean().required('Choose yes or no.'),
  contractorCount: yup
    .number()
    .transform((value, original) => (original === '' ? undefined : value))
    .typeError('Enter a number.')
    .integer('Enter a whole number.')
    .min(1, 'Enter at least 1.')
    .max(1000, 'Enter 1,000 or fewer.')
    .required('Enter how many 1099s you need.'),
  contractorStates: yup
    .array(yup.string().required())
    .min(1, 'Choose at least one state.'),
})

interface QuestionnaireValues {
  businessName: string
  hasPayrollProvider: boolean | undefined
  contractorCount: string
  contractorStates: string[]
}

/** Loads the 1099 questions for an unsubmitted job and submits the answers. */
export const useContractorFilingQuestionnaire = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const backToAnnualTaxes = useCallback(
    () => navigate(ANNUAL_TAXES_PATH),
    [navigate]
  )
  const { result: jobResult, loading: isJobLoading } =
    useFetchResponseWithUtils(getContractorFilingJob)
  const { result: questionnaire, loading: isPrefillLoading } =
    useFetchResponseWithUtils(getContractorFilingQuestionnaire)
  const isAwaitingAnswers =
    jobResult?.job?.jobStatus === TaxfyleJobStatus.underConstruction

  // Only an unsubmitted job has questions to answer.
  useEffect(() => {
    if (!isJobLoading && !isAwaitingAnswers) {
      navigate(ANNUAL_TAXES_PATH, { replace: true })
    }
  }, [isJobLoading, isAwaitingAnswers, navigate])

  // The error lives in shared fetch state, so a revisited page starts without the last one.
  useEffect(() => {
    dispatch(invalidateFetch(SUBMIT_CONTRACTOR_FILING_QUESTIONNAIRE_KEY))
  }, [dispatch])
  const submitError = useReselector(
    getFetchError,
    SUBMIT_CONTRACTOR_FILING_QUESTIONNAIRE_KEY
  )

  const initialValues = useMemo<QuestionnaireValues>(
    () => ({
      businessName: questionnaire?.prefill.businessName ?? '',
      hasPayrollProvider:
        questionnaire?.prefill.hasPayrollProvider ?? undefined,
      contractorCount: '',
      contractorStates: questionnaire?.prefill.contractorStates ?? [],
    }),
    [questionnaire]
  )

  // Validating only after a submit attempt keeps an error from appearing on blur and moving Submit from under the click.
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false)
  const formik = useFormik<QuestionnaireValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: hasTriedSubmit,
    validateOnChange: hasTriedSubmit,
    onSubmit: async (values) => {
      const res = await submitContractorFilingQuestionnaire({
        businessName: values.businessName.trim(),
        hasPayrollProvider: Boolean(values.hasPayrollProvider),
        contractorCount: Number(values.contractorCount),
        contractorStates: values.contractorStates,
      })(dispatch)
      if (res) {
        backToAnnualTaxes()
      }
    },
  })

  const submit = useCallback(() => {
    setHasTriedSubmit(true)
    formik.handleSubmit()
  }, [formik])

  return {
    isAwaitingAnswers,
    isPrefillLoading,
    prefill: questionnaire?.prefill,
    submitError,
    formik,
    submit,
    backToAnnualTaxes,
  }
}
